/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
} from '@material-ui/core'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import { useHistory } from 'react-router-dom'
import { Text } from '../../Components'
import { colors } from '../../Themes'
import { useStyles } from './Paritionor.styles'
import AddPractitionerDrawer from './AddPractitionerDrawer'
import { deletePractitioner, getPractitioners } from '../../Services/practitioners'

const STATUS_MAP = {
  ACTIVE: 'Active',
  PENDING: 'Invited',
}

function StatusBadge({ status, classes }) {
  const label = STATUS_MAP[status] ?? status
  if (label === 'Active') {
    return <span className={classes.statusActive}>{label}</span>
  }
  return <span className={classes.statusInvited}>{label}</span>
}

function Practitioners() {
  const classes = useStyles()
  const history = useHistory()
  const [practitioners, setPractitioners] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [deletingSub, setDeletingSub] = useState(null)
  const [deleteError, setDeleteError] = useState(null)

  const fetchPractitioners = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getPractitioners()
      const items = response?.data?.data?.items ?? []
      setPractitioners(items)
    } catch {
      setError('Failed to load practitioners. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchPractitioners()
  }, [])

  const handleDelete = async (sub) => {
    // Optimistically update, but reconcile with server after success.
    setDeletingSub(sub)
    setDeleteError(null)

    try {
      await deletePractitioner(sub)
      setPractitioners((prev) => prev.filter((p) => p.sub !== sub))
    } catch {
      setDeleteError('Failed to delete practitioner. Please try again.')
    } finally {
      setDeletingSub(null)
    }
  }

  const Header = () => (
    <div className={classes.headerRow}>
      <div className={classes.headerLeft}>
        <Text size="subnote" weight="bold">Practitioner List</Text>
        <Text size="footnote" color={colors.footnote}>Add and maintain the practitioner list.</Text>
      </div>
      <div className={classes.headerRight}>
        <span className={classes.counterText}>
          <span className={classes.counterBold}>{practitioners.length}</span> Practitioner
        </span>
        <button className={classes.addButton} onClick={() => setDrawerOpen(true)}>
          Add practitioner
        </button>
      </div>
    </div>
  )

  const renderBody = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={4} align="center" className={classes.tableBodyCell}>
            <CircularProgress size={28} style={{ color: colors.primary }} />
          </TableCell>
        </TableRow>
      )
    }

    if (error) {
      return (
        <TableRow>
          <TableCell colSpan={4} align="center" className={classes.tableBodyCell}>
            <Text size="footnote" color={colors.secondary}>{error}</Text>
          </TableCell>
        </TableRow>
      )
    }

    if (practitioners.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={4} align="center" className={classes.tableBodyCell}>
            <Text size="footnote" color={colors.footnote}>No practitioners added yet.</Text>
          </TableCell>
        </TableRow>
      )
    }

    return practitioners.map((row) => (
      <TableRow key={row.sub} className={classes.tableRow}>
        <TableCell className={classes.tableBodyCell}>
          <Text size="footnote">{row.practitionerName}</Text>
        </TableCell>
        <TableCell className={classes.tableBodyCell}>
          <Text size="footnote">{row.username}</Text>
        </TableCell>
        <TableCell className={classes.tableBodyCell}>
          <StatusBadge status={row.status} classes={classes} />
        </TableCell>
        <TableCell className={classes.tableBodyCell}>
          <IconButton
            className={classes.deleteButton}
            onClick={() => handleDelete(row.sub)}
            disableRipple
            size="small"
            disabled={deletingSub === row.sub}
          >
            {deletingSub === row.sub ? (
              <CircularProgress size={18} style={{ color: colors.secondary }} />
            ) : (
              <DeleteOutlineIcon className={classes.deleteIcon} />
            )}
          </IconButton>
        </TableCell>
      </TableRow>
    ))
  }

  return (
    <>
      <AddPractitionerDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSuccess={fetchPractitioners}
      />
      <div className={classes.page}>
        <div className={classes.pageHeader}>
          <Header />
        </div>
        <div className={classes.pageContent}>
          <div className={classes.infoBanner}>
            <div className={classes.infoBannerLeft}>
              <div className={classes.infoIcon}>i</div>
              <Text size="footnote" color={colors.primary}>Add more practitioners to your team.</Text>
            </div>
            <span
              className={classes.goToStore}
              onClick={() => history.push('/store')}
            >
              Go to store
            </span>
          </div>

          {deleteError && (
            <div style={{ marginTop: 12 }}>
              <Text size="footnote" color={colors.secondary}>{deleteError}</Text>
            </div>
          )}

          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table className={classes.table} aria-label="practitioners table">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeadCell}>Practitioner</TableCell>
                  <TableCell className={classes.tableHeadCell}>Username</TableCell>
                  <TableCell className={classes.tableHeadCell}>Status</TableCell>
                  <TableCell className={classes.tableHeadCell}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {renderBody()}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </>
  )
}

export default Practitioners
